import { Component, OnInit, inject, signal } from '@angular/core';

import { User, Gender } from '@shared/models/user.model';
import { UserService } from '@shared/services/user.service';
import { AvatarService } from '@shared/components/avatar/avatar.service';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';

import { takeUntil } from 'rxjs/operators';
import { EventManager } from '@app/shared/utils/event-manager';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { Observable } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '@app/shared/shared.module';
import { Heading2Component } from '@app/shared/components/text/h2/h2.component';
import { AvatarComponent } from '@app/shared/components/avatar/avatar.component';
import { AvatarUploadComponent } from '@app/shared/components/avatar/avatar-upload/avatar-upload.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { DropdownComponent, DropdownOption } from '@app/shared/components/dropdown/dropdown.component';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-manage-profile',
  templateUrl: './manage-profile.component.html',
  styleUrls: ['./manage-profile.component.scss'],
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    Heading2Component,
    AvatarComponent,
    AvatarUploadComponent,
    ButtonComponent,
    DropdownComponent,
  ],
})
export class ManageProfileComponent implements OnInit {
  user: User;
  hasEmail = signal(false);
  hasPassword = signal(false);
  uploading: boolean;
  imageUploaded = signal(false);
  genders: DropdownOption[];
  readonly defaultGenderOption: DropdownOption = { label: 'Undisclosed', value: Gender.Undisclosed };
  profileForm: FormGroup;
  componentDestroy: () => Observable<unknown>;

  private readonly userService = inject(UserService);
  private readonly avatarService = inject(AvatarService);
  private readonly eventManager = inject(EventManager);
  public readonly logger = inject(LoggerService);
  private readonly formBuilder = inject(FormBuilder);

  ngOnInit() {
    this.genders = Object.values(Gender).map((value) => ({
      label: value,
      value: value,
    }));

    this.userService.currentUser$.pipe(takeUntil(this.componentDestroy())).subscribe({
      next: (user: User) => this.setUser(user),
      error: (error) => this.logger.error(error),
    });

    this.profileForm = this.formBuilder.group({
      gender: [this.user.gender ?? this.defaultGenderOption.value],
      allowsNewsletter: [this.user.allowsNewsletter],
    });

    this.profileForm.get('gender')?.valueChanges.subscribe((value) => {
      this.user.gender = value;
    });
    this.profileForm.get('allowsNewsletter')?.valueChanges.subscribe((value) => {
      this.user.allowsNewsletter = value;
    });
  }

  private setUser(user: User) {
    this.user = Object.assign({}, user);
    this.hasEmail.set(this.user.email.indexOf('@slot.com') == -1);
    this.hasPassword.set(this.user.hasPassword);
  }

  uploadStateChanged(state: boolean): void {
    if (state) {
      this.uploading = true;
    } else {
      this.uploading = false;
      this.avatarService.notifyAvatar(this.user.id);
    }

    this.imageUploaded.set(true);
  }

  onSubmit(): void {
    this.userService
      .updateUser(this.user)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: () => {
          this.userService.setCurrentUser(this.user);
        },
        error: (error) => this.logger.error(error),
      });

    this.close();
  }

  managePassword() {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.ManagePasswordComponent, ModalType.View, {}, 'default')
    );
  }

  deleteAccount() {
    this.eventManager.openModal(new Modal(NonPersistedModalName.AccountDeletionStep1, ModalType.View, {}, 'default'));
  }

  close() {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.ManageProfileComponent, ModalType.View));
  }
}
